import { useEffect, useState } from "react";
import { ENGAGEMENTS_MAX, LIBELLES_MOMENT, MOMENTS_ENGAGEMENT } from "@riseafrica/scoring";
import type { MomentEngagement } from "@riseafrica/scoring";
import {
  lireProgression,
  mesurerProgression,
  noterRecommandation,
  prendreEngagements,
  repondreEtape,
} from "../lib/api";
import { lienDeParrainage } from "../lib/share";
import type {
  ApercuProgression,
  LigneDeBasePublique,
  RefusProgression,
  ReponseEtape,
  SuiteEtape,
} from "../lib/api";
import { fmt } from "../lib/format";
import { track } from "../lib/track";
import { useApp } from "../state/AppState";

/**
 * La page de progression à quatre-vingt-dix jours — 28/09/2026.
 *
 * On y arrive par le lien imprimé dans le rapport, ou par le rendez-vous des
 * quatre-vingt-dix jours. Deux moments, un seul écran :
 *
 * - **avant soixante jours**, on s'engage : une à trois actions du rapport, le
 *   moment où on les fera, et la durée de la dernière fois ;
 * - **à partir de soixante jours**, on redéclare ses heures, pré-remplies, et
 *   l'écart de l'indice s'affiche gratuitement. Le détail — tâche par tâche,
 *   engagements tenus ou non, dix nouvelles actions — est le rapport de
 *   progression, au prix du rapport.
 *
 * Rien n'y est demandé de nominatif. Le lien est la clé.
 */

const DATE: Intl.DateTimeFormatOptions = { day: "numeric", month: "long", year: "numeric" };

/** Un écart d'indice dit en clair : le signe, puis ce qu'il veut dire. */
function lireEcart(ecart: number): string {
  if (ecart < 0) return `${fmt(Math.abs(ecart))} point${Math.abs(ecart) >= 2 ? "s" : ""} de moins : votre exposition a baissé depuis trois mois.`;
  if (ecart > 0) return `${fmt(ecart)} point${ecart >= 2 ? "s" : ""} de plus : votre temps s'est déplacé vers des tâches plus automatisables.`;
  return "Aucun écart : votre répartition du temps n'a pas bougé.";
}

interface Choix {
  moment: MomentEngagement;
  minutes: string;
}

function minutesOuNull(saisie: string): number | null {
  const n = Number(saisie.replace(",", "."));
  return saisie.trim() === "" || !Number.isFinite(n) || n <= 0 ? null : Math.round(n);
}

export function Progression({ jeton }: { jeton: string }) {
  const { openPay, setProfilProgression, setASonCompte, say } = useApp();
  const [base, setBase] = useState<LigneDeBasePublique | null>(null);
  const [refus, setRefus] = useState<RefusProgression | null>(null);

  useEffect(() => {
    document.title = "Votre progression | STRATERA";
    let vivant = true;
    void lireProgression(jeton).then((r) => {
      if (!vivant) return;
      if ("refus" in r) {
        setRefus(r.refus);
        return;
      }
      setBase(r.base);
      setASonCompte(r.base.aSonCompte);
      track("progression_ouverte", { mesureOuverte: r.base.mesureOuverte });
    });
    return () => {
      vivant = false;
    };
  }, [jeton, setASonCompte]);

  return (
    <section id="contenu">
      <div className="shell narrow app">
        <div className="kicker">Votre progression</div>
        {refus !== null && <Refus refus={refus} />}
        {base === null && refus === null && <p className="lede">Chargement de votre page…</p>}
        {base !== null && (
          <>
            <h1 style={{ marginTop: "6px" }}>{base.metier.charAt(0).toUpperCase() + base.metier.slice(1)}</h1>
            <p className="lede">
              Rapport du {new Date(base.creeLe).toLocaleDateString("fr-FR", DATE)}. Rendez-vous de mesure
              le {new Date(base.rendezVousLe).toLocaleDateString("fr-FR", DATE)}.
            </p>
            {base.mesureOuverte ? (
              <Mesure
                jeton={jeton}
                base={base}
                onAcheter={(heures, minutes) => {
                  setProfilProgression({
                    metier: base.metier,
                    metierNormalise: base.metier,
                    groupe: base.groupe,
                    taches: base.taches.map((t, i) => ({ texte: t.texte, niveau: t.niveau, heures: heures[i] })),
                    valeurAnnuelle: null,
                    maitrise: null,
                    aSonCompte: base.aSonCompte,
                    gestion: base.gestion,
                    titres: null,
                    progression: { jeton, minutes },
                  });
                  openPay("report");
                }}
              />
            ) : (
              <>
              {base.pointEtapeOuvert && <PointEtape jeton={jeton} dejaRepondu={base.etape30} />}
              <Engagements
                jeton={jeton}
                base={base}
                onEngage={(engagements) => {
                  setBase({ ...base, engagements });
                  say("Vos engagements sont enregistrés.");
                }}
              />
              </>
            )}
          </>
        )}
        <p className="fine" style={{ marginTop: "24px" }}>
          <a href="/">Retour au diagnostic</a> · <a href="/methode">La méthode de calcul</a>
        </p>
      </div>
    </section>
  );
}

function Refus({ refus }: { refus: RefusProgression }) {
  if (refus === "indisponible") {
    return (
      <div className="err">
        La page n'a pas pu se charger. Réessayez dans un instant : votre lien reste valable.
      </div>
    );
  }
  return (
    <div className="block">
      <h2>{refus === "fermee" ? "Le suivi de progression n'est pas ouvert" : "Cette page n'existe plus"}</h2>
      <p>
        {refus === "fermee"
          ? "Le suivi à quatre-vingt-dix jours n'est pas proposé en ce moment. Vous pouvez refaire votre diagnostic gratuitement depuis l'accueil."
          : "Ce lien ne correspond à aucune page de progression, ou elle a expiré. Une page de progression est gardée deux cents jours après le rapport, puis effacée."}
      </p>
    </div>
  );
}

/** Le message transféré, écrit à la place de la personne. */
export const MESSAGE_RECOMMANDATION =
  "J'ai mesuré mon métier face à l'IA, tâche par tâche, en heures et en francs. C'est gratuit et ça prend deux minutes. Fais le tien :";

/**
 * La recommandation, au moment où la valeur vient d'être ressentie — 28/09/2026.
 *
 * Elle n'apparaît qu'après un « Commencé » au point d'étape, ou après une
 * mesure qui montre que l'exposition a baissé : c'est à ces instants-là que la
 * même demande rend le plus. Deux gestes, tous deux facultatifs :
 *
 * - une note de 0 à 10, la question qui dit si le bouche-à-oreille peut tenir ;
 * - un message WhatsApp déjà écrit, avec le lien de parrainage de la visite.
 *
 * Aucune récompense n'est promise : il n'y en a pas.
 */
function Recommander({ jeton }: { jeton: string }) {
  const [note, setNote] = useState<number | null>(null);

  const noter = (valeur: number) => {
    setNote(valeur);
    track(valeur >= 9 ? "note_promoteur" : valeur >= 7 ? "note_passif" : "note_detracteur");
    void noterRecommandation(jeton, valeur);
  };

  return (
    <div className="block recommander">
      <h2>Qui, autour de vous, devrait faire ce diagnostic ?</h2>
      <p>
        Une personne de votre équipe, un collègue qui passe ses semaines sur les mêmes tâches, quelqu'un à son
        compte dans votre métier. Le message est déjà écrit :
      </p>
      <blockquote className="fine">
        « {MESSAGE_RECOMMANDATION} {lienDeParrainage()} »
      </blockquote>
      <button
        type="button"
        className="btn btn-wa"
        onClick={() => {
          track("recommandation_envoyee");
          window.open(
            `https://wa.me/?text=${encodeURIComponent(`${MESSAGE_RECOMMANDATION} ${lienDeParrainage()}`)}`,
            "_blank",
          );
        }}
      >
        Envoyer sur WhatsApp
      </button>

      <fieldset style={{ border: 0, padding: 0, margin: "20px 0 0" }}>
        <legend>
          <b>Recommanderiez-vous STRATERA à un collègue ?</b> De 0 (pas du tout) à 10 (sans hésiter).
        </legend>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "8px" }}>
          {Array.from({ length: 11 }, (_, i) => (
            <button
              key={i}
              type="button"
              className={note === i ? "btn btn-brass" : "btn btn-ghost"}
              aria-pressed={note === i}
              style={{ minWidth: "44px", padding: "8px 0" }}
              onClick={() => noter(i)}
            >
              {i}
            </button>
          ))}
        </div>
      </fieldset>
      <div role="status" aria-live="polite" className="fine">
        {note !== null && "Merci. Votre note est enregistrée, sans rien qui vous désigne."}
      </div>
    </div>
  );
}

const REPONSES: ReadonlyArray<{ valeur: ReponseEtape; libelle: string; evenement: "etape_commencee" | "etape_pas_encore" | "etape_abandonnee" }> = [
  { valeur: "commence", libelle: "Commencé", evenement: "etape_commencee" },
  { valeur: "pas_encore", libelle: "Pas encore", evenement: "etape_pas_encore" },
  { valeur: "abandonne", libelle: "Abandonné", evenement: "etape_abandonnee" },
];

/**
 * Le point d'étape à trente jours : une question, trois boutons, une suite.
 *
 * La réponse se donne ici, par un bouton, et jamais par le lien du courriel :
 * un filtre de messagerie qui ouvre les liens pour les inspecter répondrait à
 * la place de la personne.
 */
function PointEtape({ jeton, dejaRepondu }: { jeton: string; dejaRepondu: ReponseEtape | null }) {
  const [suite, setSuite] = useState<SuiteEtape | null>(null);
  const [reponse, setReponse] = useState<ReponseEtape | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(false);

  const repondre = async (r: (typeof REPONSES)[number]) => {
    setEnvoi(true);
    setErreur(false);
    const resultat = await repondreEtape(jeton, r.valeur);
    setEnvoi(false);
    if ("erreur" in resultat) {
      setErreur(true);
      return;
    }
    track(r.evenement);
    setReponse(r.valeur);
    setSuite(resultat.suite);
  };

  return (
    <div className="block">
      <h2>Point d'étape : avez-vous commencé ?</h2>
      {dejaRepondu !== null && suite === null && (
        <p className="fine">Vous avez déjà répondu. Vous pouvez répondre de nouveau si les choses ont changé.</p>
      )}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {REPONSES.map((r) => (
          <button
            key={r.valeur}
            type="button"
            className="btn btn-ghost"
            disabled={envoi}
            onClick={() => void repondre(r)}
          >
            {r.libelle}
          </button>
        ))}
      </div>
      {erreur && <div className="err">La réponse n'a pas été enregistrée. Réessayez dans un instant.</div>}
      <div role="status" aria-live="polite">
        {suite && (
          <div className="refer" style={{ marginTop: "16px" }}>
            <b>{suite.titre}</b>
            {suite.texte}
            {suite.modifier && (
              <p className="fine" style={{ marginTop: "8px" }}>
                Vous pouvez changer d'engagement juste en dessous.
              </p>
            )}
          </div>
        )}
      </div>
      {reponse === "commence" && <Recommander jeton={jeton} />}
    </div>
  );
}

/** Avant soixante jours : choisir une à trois actions, et quand. */
function Engagements({
  jeton,
  base,
  onEngage,
}: {
  jeton: string;
  base: LigneDeBasePublique;
  onEngage: (engagements: LigneDeBasePublique["engagements"]) => void;
}) {
  const [edition, setEdition] = useState(base.engagements.length === 0);
  const [choix, setChoix] = useState<Record<number, Choix>>(() =>
    Object.fromEntries(
      base.engagements.map((e) => [
        e.action,
        { moment: e.moment as MomentEngagement, minutes: e.minutesAvant === null ? "" : String(e.minutesAvant) },
      ]),
    ),
  );
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const retenues = Object.keys(choix).map(Number);

  const basculer = (i: number) => {
    setChoix((c) => {
      if (i in c) {
        const { [i]: _, ...reste } = c;
        return reste;
      }
      if (Object.keys(c).length >= ENGAGEMENTS_MAX) return c;
      return { ...c, [i]: { moment: "a_chaque_fois_que_la_tache_arrive", minutes: "" } };
    });
  };

  const enregistrer = async () => {
    setEnvoi(true);
    setErreur(null);
    const r = await prendreEngagements(
      jeton,
      retenues.map((i) => ({ action: i, moment: choix[i].moment, minutes: minutesOuNull(choix[i].minutes) })),
    );
    setEnvoi(false);
    if ("erreur" in r) {
      setErreur("L'enregistrement n'a pas abouti. Réessayez dans un instant.");
      return;
    }
    track("progression_engagee", { nombre: r.engagements.length });
    setEdition(false);
    onEngage(r.engagements);
  };

  if (!edition) {
    return (
      <div className="block">
        <h2>Vos engagements</h2>
        <ul>
          {base.engagements.map((e) => (
            <li key={e.action}>
              <b>Quand : {LIBELLES_MOMENT[e.moment as MomentEngagement]}</b> — je fais : {e.titre}
            </li>
          ))}
        </ul>
        <p className="fine">
          La mesure s'ouvre dans {fmt(base.joursAvantMesure)} jour{base.joursAvantMesure >= 2 ? "s" : ""}. Revenez
          alors par ce même lien : vos tâches et vos heures seront déjà remplies.
        </p>
        <button type="button" className="btn btn-ghost" onClick={() => setEdition(true)}>
          Modifier mes engagements
        </button>
      </div>
    );
  }

  return (
    <div className="block">
      <h2>Retenez une, deux ou trois des actions de votre rapport, et le moment où vous les ferez</h2>
      <p className="sub">
        Un plan de dix actions s'oublie ; un engagement de la forme « quand …, je fais … » se tient. Dans
        trois mois, votre rapport de progression dira lesquels ont rendu du temps.
      </p>
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="sr">Actions de votre rapport</legend>
        {base.actions.map((a, i) => {
          const pris = i in choix;
          const plein = !pris && retenues.length >= ENGAGEMENTS_MAX;
          return (
            <div key={`${a.titre}-${i}`} style={{ marginBottom: "14px" }}>
              <label style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                <input type="checkbox" checked={pris} disabled={plein} onChange={() => basculer(i)} />
                <span>
                  <b>{a.titre}</b>
                  <br />
                  <span className="fine">Tâche visée : {a.tache}</span>
                </span>
              </label>
              {pris && (
                <div style={{ marginLeft: "28px", marginTop: "8px", display: "grid", gap: "8px" }}>
                  <div className="field">
                    <label htmlFor={`moment-${i}`}>Quand</label>
                    <select
                      id={`moment-${i}`}
                      value={choix[i].moment}
                      onChange={(e) =>
                        setChoix((c) => ({ ...c, [i]: { ...c[i], moment: e.target.value as MomentEngagement } }))
                      }
                    >
                      {MOMENTS_ENGAGEMENT.map((m) => (
                        <option key={m} value={m}>
                          {LIBELLES_MOMENT[m]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label htmlFor={`minutes-${i}`} className="label-phrase">
                      La dernière fois que vous avez fait « {a.tache} », combien de minutes cela a-t-il pris ?
                      Facultatif.
                    </label>
                    <input
                      id={`minutes-${i}`}
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={600}
                      value={choix[i].minutes}
                      onChange={(e) => setChoix((c) => ({ ...c, [i]: { ...c[i], minutes: e.target.value } }))}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </fieldset>
      <p className="fine">
        {retenues.length}/{ENGAGEMENTS_MAX} action{retenues.length >= 2 ? "s" : ""} retenue
        {retenues.length >= 2 ? "s" : ""}. Rien de nominatif n'est demandé.
      </p>
      {erreur && <div className="err">{erreur}</div>}
      <button
        type="button"
        className="btn btn-brass"
        disabled={retenues.length === 0 || envoi}
        onClick={() => void enregistrer()}
      >
        {envoi ? "Enregistrement…" : "Enregistrer mes engagements"}
      </button>
    </div>
  );
}

/** À partir de soixante jours : redéclarer, voir l'écart, acheter le détail. */
function Mesure({
  jeton,
  base,
  onAcheter,
}: {
  jeton: string;
  base: LigneDeBasePublique;
  onAcheter: (heures: number[], minutes: Array<number | null>) => void;
}) {
  const { reportPrice } = useApp();
  const [heures, setHeures] = useState<number[]>(() => base.taches.map((t) => t.heures));
  const [minutes, setMinutes] = useState<string[]>(() => base.engagements.map(() => ""));
  const [apercu, setApercu] = useState<ApercuProgression | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const mesurer = async () => {
    setEnvoi(true);
    setErreur(null);
    const r = await mesurerProgression(jeton, heures, minutes.map(minutesOuNull));
    setEnvoi(false);
    if ("erreur" in r) {
      setErreur(
        r.erreur === "heures_invalides"
          ? "Déclarez au moins une heure sur une tâche."
          : "La mesure n'a pas abouti. Réessayez dans un instant.",
      );
      return;
    }
    track("progression_mesuree", { ecart: r.apercu.ecartScore });
    setApercu(r.apercu);
  };

  return (
    <>
      <div className="block">
        <h2>Vos heures d'aujourd'hui</h2>
        <p className="sub">
          Ce sont vos heures d'il y a trois mois. Corrigez seulement ce qui a changé : les tâches et le barème
          restent les mêmes, c'est ce qui rend l'écart lisible.
        </p>
        {base.taches.map((t, i) => (
          <div className="slider" key={`${t.texte}-${i}`}>
            <div className="top">
              <span className="name" id={`tache-${i}`}>
                {t.texte}
              </span>
              <span className="val">{fmt(heures[i])} h</span>
            </div>
            <input
              type="range"
              min={0}
              max={Math.max(20, Math.ceil(t.heures))}
              step={0.5}
              value={heures[i]}
              aria-labelledby={`tache-${i}`}
              aria-valuetext={`${fmt(heures[i])} heures par semaine`}
              onChange={(e) => {
                setApercu(null);
                setHeures((h) => h.map((v, j) => (j === i ? Number(e.target.value) : v)));
              }}
            />
          </div>
        ))}
        {base.engagements.length > 0 && (
          <>
            <h3 style={{ marginTop: "18px" }}>Vos engagements</h3>
            {base.engagements.map((e, i) => (
              <div className="field" key={e.action}>
                <label htmlFor={`occurrence-${i}`} className="label-phrase">
                  Quand : {LIBELLES_MOMENT[e.moment as MomentEngagement]} — je fais : {e.titre}. La dernière fois
                  que vous avez fait « {e.tache} », combien de minutes ? Facultatif.
                </label>
                <input
                  id={`occurrence-${i}`}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={600}
                  value={minutes[i]}
                  onChange={(ev) => {
                    setApercu(null);
                    setMinutes((m) => m.map((v, j) => (j === i ? ev.target.value : v)));
                  }}
                />
              </div>
            ))}
          </>
        )}
        {erreur && <div className="err">{erreur}</div>}
        <button type="button" className="btn btn-brass" disabled={envoi} onClick={() => void mesurer()}>
          {envoi ? "Calcul…" : "Voir mon écart"}
        </button>
      </div>

      <div role="status" aria-live="polite">
        {apercu && (
          <div className="block">
            <div className="verdict">
              <div className="kicker">Votre indice</div>
              <div className="score" style={{ fontSize: "40px" }}>
                {apercu.scoreAvant} → {apercu.scoreApres}
              </div>
              <div className="rank">{lireEcart(apercu.ecartScore)}</div>
            </div>
            <p className="fine">
              Calculé par le même barème, sur les mêmes tâches, à partir de ce que vous déclarez. Refaire la
              mesure est gratuit, autant de fois que vous le voulez.
            </p>
          </div>
        )}
      </div>

      {apercu && (
        <div className="block">
          <h2>Le rapport de progression — {fmt(reportPrice)} F</h2>
          <p>
            L'écart vous dit <b>que</b> quelque chose a bougé. Le rapport vous dit <b>où</b>, <b>combien</b> et{" "}
            <b>quoi faire ensuite</b> :
          </p>
          <ul>
            <li>vos heures tâche par tâche, avant et aujourd'hui ;</li>
            <li>vos engagements, et le temps que chacun a rendu ;</li>
            <li>dix nouvelles actions qui partent de votre situation d'aujourd'hui, et cinq automatisations ;</li>
            <li>une nouvelle page de progression pour les trois mois suivants.</li>
          </ul>
          <p className="fine">
            Les heures sont celles que vous déclarez : le rapport les compare, il ne les certifie pas.
            Remboursé sur simple demande, sans justification.
          </p>
          <button
            type="button"
            className="btn btn-brass"
            onClick={() => onAcheter(heures, minutes.map(minutesOuNull))}
          >
            Obtenir mon rapport de progression
          </button>
        </div>
      )}

      {/* Après l'offre, pas avant : l'achat d'abord, la recommandation ensuite,
          et seulement quand l'exposition a baissé. */}
      {apercu && apercu.ecartScore < 0 && <Recommander jeton={jeton} />}
    </>
  );
}
